/**
 * Props-driven record form body for the App workspace host.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import { RuntimeFormRenderer, resolveFormSchema } from '@/runtime'
import { useAppWorkspaceStore } from './appWorkspaceStore'
import { resolveOpenMode } from './resolveOpenMode'
import { useMyPermissions } from '@/app/permissions/useMyPermissions'
import { WorkspaceEmptyGate } from './WorkspaceEmptyGate'

export interface RuntimeFormBodyProps {
  connectionId: string
  objectName: string
  recordId: string | null
  formName?: string
  /** Workspace tab path — when omitted (thin page wrappers), skip openMode resolution. */
  tabPath?: string
  presentation?: 'tab' | 'modal'
  onClose: () => void
  onSaved?: (
    record: Record<string, unknown>,
    opts?: { willClose?: boolean },
  ) => void
  onDirtyChange?: (dirty: boolean) => void
  onTitleChange?: (title: string) => void
  onStatusChange?: (status: string | undefined) => void
  onTogglePresentation?: () => void
}

export const RuntimeFormBody: React.FC<RuntimeFormBodyProps> = ({
  connectionId,
  objectName,
  recordId,
  formName: formNameProp,
  tabPath,
  presentation,
  onClose,
  onSaved,
  onDirtyChange,
  onTitleChange,
  onStatusChange,
  onTogglePresentation,
}) => {
  const location = useLocation()
  // Effective rights (Task 12, ERP Users & Access Rights epic) — fetched once
  // here so RuntimeFormRenderer itself stays hook-free (its existing suites
  // need no react-query/connection provider).
  const { perms } = useMyPermissions()
  const [resolvedFormName, setResolvedFormName] = useState<string | undefined>(formNameProp)
  const formName = formNameProp || resolvedFormName

  // Capture create seed once from navigation state (hierarchy Group|Element).
  const createSeed = useMemo(() => {
    const state = location.state as { createSeed?: Record<string, unknown> } | null
    return state?.createSeed ?? null
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- mount-only

  useEffect(() => {
    if (formNameProp) {
      setResolvedFormName(formNameProp)
    }
  }, [formNameProp])

  // Resolve openMode once schema is known; never overwrite a user toggle.
  // Re-run when this tab becomes active while still unresolved — heals a
  // zombie /new left by a replacePath race (Create looked like a no-op).
  const isActiveUnresolved = useAppWorkspaceStore((s) => {
    if (!tabPath || s.activePath !== tabPath) return false
    return s.tabs.some((t) => t.path === tabPath && t.presentation === 'unresolved')
  })
  useEffect(() => {
    if (!connectionId || !objectName || !tabPath || !isActiveUnresolved) return
    let cancelled = false
    resolveFormSchema(connectionId, objectName, 'item', formNameProp)
      .then((form) => {
        if (cancelled) return
        if (form?.schema?.name && !formNameProp) {
          setResolvedFormName(form.schema.name)
        }
        const current = useAppWorkspaceStore
          .getState()
          .tabs.find((t) => t.path === tabPath)
        if (current?.presentation !== 'unresolved') return
        useAppWorkspaceStore
          .getState()
          .setPresentation(tabPath, resolveOpenMode(form?.schema))
      })
      .catch(() => {
        if (cancelled) return
        const current = useAppWorkspaceStore
          .getState()
          .tabs.find((t) => t.path === tabPath)
        if (current?.presentation !== 'unresolved') return
        useAppWorkspaceStore.getState().setPresentation(tabPath, 'tab')
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, objectName, formNameProp, tabPath, isActiveUnresolved])

  const isActiveTab = useAppWorkspaceStore((s) =>
    tabPath ? s.activePath === tabPath : false,
  )
  const liftChrome = Boolean(tabPath && presentation === 'tab' && isActiveTab)

  if (!connectionId || !objectName) {
    return (
      <WorkspaceEmptyGate reason={!connectionId ? 'connection' : 'object'} />
    )
  }

  const isNew = recordId == null

  return (
    <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <RuntimeFormRenderer
        connectionId={connectionId}
        objectName={objectName}
        recordId={recordId}
        formName={formName}
        initialValues={isNew ? createSeed : null}
        presentation={presentation}
        onClose={onClose}
        onSaved={onSaved}
        onDirtyChange={onDirtyChange}
        onTitleChange={onTitleChange}
        onStatusChange={onStatusChange}
        liftChrome={liftChrome}
        onTogglePresentation={onTogglePresentation}
        permissions={perms}
      />
    </Box>
  )
}
